import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { FiUsers, FiBook, FiAward, FiPlus } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      const [classrooms, quizzes] = await Promise.all([
        axios.get('/api/classrooms'),
        axios.get('/api/quizzes')
      ]);
      
      return {
        classroomCount: classrooms.data.classrooms.length,
        quizCount: quizzes.data.quizzes.length,
        activeQuizCount: quizzes.data.quizzes.filter(q => q.isActive).length
      };
    }
  });

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.name}!
        </h1>
        <p className="mt-2 text-gray-600">
          Here's an overview of your {isAdmin ? 'admin' : 'student'} dashboard
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {isAdmin ? 'My Classrooms' : 'Joined Classrooms'}
              </p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {stats?.classroomCount || 0}
              </p>
            </div>
            <div className="p-3 bg-primary-100 rounded-full">
              <FiUsers className="text-primary-600" size={24} />
            </div>
          </div>
          <Link
            to="/classrooms"
            className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center"
          >
            View classrooms →
          </Link>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {isAdmin ? 'Created Quizzes' : 'Available Quizzes'}
              </p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {stats?.activeQuizCount || 0}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <FiBook className="text-green-600" size={24} />
            </div>
          </div>
          <Link
            to="/quizzes"
            className="mt-4 text-sm text-green-600 hover:text-green-700 font-medium inline-flex items-center"
          >
            View quizzes →
          </Link>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Quizzes
              </p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {stats?.quizCount || 0}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <FiAward className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/classrooms/create"
            className="card hover:shadow-lg transition-shadow border-2 border-dashed border-gray-300 hover:border-primary-400 text-center"
          >
            <FiPlus className="mx-auto text-gray-400 mb-2" size={32} />
            <p className="text-lg font-medium text-gray-700">Create New Classroom</p>
            <p className="text-sm text-gray-500 mt-1">Start a new classroom for students</p>
          </Link>

          <Link
            to="/quizzes/create"
            className="card hover:shadow-lg transition-shadow border-2 border-dashed border-gray-300 hover:border-primary-400 text-center"
          >
            <FiPlus className="mx-auto text-gray-400 mb-2" size={32} />
            <p className="text-lg font-medium text-gray-700">Create New Quiz</p>
            <p className="text-sm text-gray-500 mt-1">Design a new quiz for your students</p>
          </Link>
        </div>
      )}
    </div>
  );
};

export default Dashboard;